<template>
    <div class="files org_settings " id="public-file-upload-page">
        <div class="card mb-3">
            <FileDropzone
                :client-url="client.url"
                :on-file-uploaded="handleFileUploaded"
                @fileUploading="handleFileUploading"
                @uploadProgress="handleUploadProgress"
            />
        </div>
        
        <!-- Recently Uploaded Files Table -->
        <div v-if="recent_files.length > 0" class="mt-4">
            <div class="card table-card">
                <div class="card-header">
                    <div class="d-flex flex-row justify-content-between">
                        <div class="d-flex justify-content-start align-items-center flex-grow-1">
                            <h4>Recently Uploaded Files ({{ recent_files.length }})</h4>
                        </div>
                    </div>
                </div>
                <div class="card-body p-0">
                    <div class="table-width-responsive">
                        <table class="table table-without-pagination m-0">
                            <thead>
                                <tr class="table-header-row">
                                    <th @click="sortByKey('name')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            File Name
                                            <div class="d-flex flex-column">
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-up.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }" alt=""
                                                >
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-down.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }" alt=""
                                                >                                                            
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortByKey('status')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Status
                                            <div class="d-flex flex-column">
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-up.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'status' && sort_order === 'asc' }" alt=""
                                                >
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-down.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'status' && sort_order === 'des' }" alt=""
                                                >                                                        
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortByKey('created_at')" class="d-none d-md-table-cell">
                                        <div class="d-flex flex-row">
                                            Uploaded At
                                            <div class="d-flex flex-column">
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-up.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'created_at' && sort_order === 'asc' }" alt=""
                                                >
                                                <img 
                                                    class="sort-key" 
                                                    src="/img/icons/avid-icons/chevron-down.svg" 
                                                    :class="{ 'sort-key-active': sort_key === 'created_at' && sort_order === 'des' }" alt=""
                                                >                                                            
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(f, index) in sorted_recent_files" :key="f.id">
                                    <td class="ps-3 align-middle d-table-cell" style="max-width: 180px;">
                                        <div class="d-flex flex-column">
                                            <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="f.name" data-bs-toggle="tooltip">
                                                {{ f.name }}
                                            </div>
                                            <!-- Progress bar for uploading files -->
                                            <div v-if="f.status === 'processing' && upload_progress[f.name] !== undefined" class="mt-1">
                                                <div class="progress" style="height: 10px;">
                                                    <div 
                                                        class="progress-bar" 
                                                        :style="{ width: upload_progress[f.name] + '%' }"
                                                        role="progressbar" 
                                                        :aria-valuenow="upload_progress[f.name]" 
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="align-middle text-nowrap d-table-cell">
                                        <span v-if="f.id >= 0 && (f.status === 'ready' || f.status === 'invalid' || f.status === 'error')" class="badge" 
                                            :class=" getColorClass(f.status, f)">
                                            {{ propercase(f.status) }}
                                            <span v-if="(f.status === 'invalid' || f.status === 'error') && f.status_msg" class="ms-1">
                                                <i class="fa fa-question-circle" data-bs-toggle="tooltip"
                                                    :title="f.status_msg + (f.error_reference_id ? ' (Reference: ' + f.error_reference_id + ')' : '')"></i>
                                            </span>
                                        </span>
                                        <span v-else class="badge" 
                                        :class="getColorClass(f.status, f)">
                                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"></div>
                                            <span class="ms-1">
                                                {{ getStatusText(f.status, f) }}...    
                                            </span>
                                        </span>
                                    </td>
                                    <td class="align-middle d-none d-md-table-cell">{{ datetime(f.created_at) }}</td>
                                </tr>
                            </tbody>
                        </table>                        
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import FileDropzone from '../components/FileDropzone.vue';
import Swal from 'sweetalert2';

export default {
    name: 'FileUpload',
    components: {
        FileDropzone
    },

    props: {
        client: {
            type: Object
        }
    },

    data() {
        return {
            recent_files: [],
            sort_key: 'name',
            sort_order: 'asc',
            upload_progress: {} // Track upload progress by filename
        };
    },

    beforeMount() {
        this.listenForFileUpdates();
    },

    computed: {
        sorted_recent_files() {
            return [...this.recent_files].sort((a, b) => {
                let aVal = a[this.sort_key];
                let bVal = b[this.sort_key];
                
                if (this.sort_key === 'created_at') {
                    aVal = new Date(aVal);
                    bVal = new Date(bVal);
                }

                return this.sort_order === 'asc'
                    ? aVal < bVal ? -1 : aVal > bVal ? 1 : 0
                    : aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            });
        }
    },
    mounted() {
        // Initialize tooltips
        this.initTooltips();
        
        // Add navigation warning for processing files
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    updated() {
        // Re-initialize tooltips after component updates
        this.$nextTick(() => {
            this.initTooltips();
        });
    },
    beforeUnmount() {
        // Clean up Pusher subscription
        window.Echo.leaveChannel(`file.${this.client.url}`);
        
        // Remove navigation warning for processing files
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    },
    methods: {
        initTooltips() {
            // Dispose of existing tooltips first to prevent memory leaks
            const existingTooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            existingTooltips.forEach(el => {
                const tooltip = bootstrap.Tooltip.getInstance(el);
                if (tooltip) {
                    tooltip.dispose();
                }
            });
            
            // Create new tooltips
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
        },
        listenForFileUpdates() {
            // Subscribe to file status updates
            window.Echo.channel(`file.${this.client.url}`)
                .listen('FileStatusUpdated', (e) => {
                    // Update file in recent_files if it exists
                    const index = this.recent_files.findIndex(f => f.id === e.id);
                    if (index !== -1) {
                        // Use Vue.set or array splice to ensure reactivity
                        this.recent_files.splice(index, 1, {
                            ...this.recent_files[index],
                            ...e
                        });
                    } else {
                        // If not found by ID, check for files with same name and 'processing' status
                        const processingFiles = this.recent_files.filter(f => 
                            f.name === e.name
                        );
                        
                        if (processingFiles.length > 0) {
                            // Sort by created_at to get the oldest one
                            processingFiles.sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));
                            const oldestProcessingFile = processingFiles[0];
                            
                            // Find the index of the oldest processing file
                            const oldestIndex = this.recent_files.findIndex(f => f.id === oldestProcessingFile.id);
                            
                            if (oldestIndex !== -1) {
                                // Update the oldest processing file with the new data
                                this.recent_files.splice(oldestIndex, 1, {
                                    ...oldestProcessingFile,
                                    ...e
                                });
                            } else {
                                this.recent_files.unshift(e);
                            }
                        } else {
                            this.recent_files.unshift(e);
                        }
                    }
                });
        },
        handleFileUploaded(file) {
            this.if_uploaded = true;
            // Clean up progress tracking for completed upload
            if (this.upload_progress[file.name]) {
                delete this.upload_progress[file.name];
            }
        },
        handleFileUploading(file){
            file.status = 'processing';
            file.progress = 0; // Initialize progress
            this.recent_files.unshift(file);
            // Initialize progress tracking
            this.upload_progress[file.name] = 0;
        },
        handleUploadProgress(progressData) {
            // Update progress for the file being uploaded
            this.upload_progress[progressData.fileName] = progressData.progress;
            
            // Also update the file in recent_files if it exists
            const index = this.recent_files.findIndex(f => f.name === progressData.fileName);
            if (index !== -1) {
                this.recent_files[index].progress = progressData.progress;
            }
        },
        
        getColorClass(word, file) {
            if(word === 'received' || word === 'processing' || word === 'analyzing') {
                return 'badge-warning';
            }
            if(word === 'ready') {
                // If it's an xlsx file that's ready, it's actually extracting worksheets, so show warning
                if (file && file.type === 'xlsx') {
                    return 'badge-warning';
                }
                return 'bg-green bg-icon-success';
            }
            return 'bg-red bg-icon-danger';
        },
        isDataFile(file) {
            return ['csv', 'tsv', 'tab', 'txt'].includes(file.type);
        },
        propercase(str) {
            if (!str) return '';
            return str.replace(/\w\S*/g, function(txt) {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
            });
        },
        getStatusText(status, file) {
            if (status === 'processing') {
                return 'Uploading';
            }
            if (status === 'received' && this.isDataFile(file)) {
                return 'Loading Data';
            }
            return this.propercase(status);
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
        },
        handleBeforeUnload(event) {
            // Check if there are any files still processing
            const hasProcessingFiles = this.recent_files.some(file => !['ready', 'invalid', 'error'].includes(file.status));
            
            if (hasProcessingFiles) {
                // Show SweetAlert confirmation
                Swal.fire({
                    title: 'Files Still Processing',
                    text: 'Some files are still being processed. Navigating away will cancel the upload. Are you sure you want to continue?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Leave Anyway',
                    cancelButtonText: 'Stay on Page',
                    customClass: {
                        popup: 'swal-warning'
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Allow navigation
                        window.removeEventListener('beforeunload', this.handleBeforeUnload);
                        window.location.href = window.location.href;
                    }
                });
                
                // Prevent default navigation
                event.preventDefault();
                event.returnValue = '';
                return false;
            }
        }
    }
}
</script>